import { ServiceException } from '@inter-ikea-kompis/exceptions';
import ServiceFetch from '../../../utilities/ServiceFetch.js';
const DEFAULT_CONFIGURATION_VERSION = '1.0';
const DEFAULT_ICF_VERSION = '1.0';
/**
 * This class is used for saving planner configurations to the IKEA VPC service.
 */
export default class DexfVpcService {
    /**
     * Constructor.
     *
     * @param options Options.
     * @param options.dexfApplicationId DEXF application ID.
     * @param options.endpoint Endpoint.
     * @param options.dexfApiKey DEXF API key.
     * @param [options.dexfConfigurationVersion] Configuration version. Defaults to "1.0".
     * @param [options.dexfIcfVersion] Icf version. Defaults to "1.0".
     */
    constructor(options) {
        this.dexfConfigurationVersion = '1.0';
        this.dexfIcfVersion = '1.0';
        this.dexfApplicationId = options.dexfApplicationId;
        this.endpoint = options.endpoint;
        this.dexfApiKey = options.dexfApiKey;
        this.dexfConfigurationVersion =
            options.dexfConfigurationVersion || DEFAULT_CONFIGURATION_VERSION;
        this.dexfIcfVersion = options.dexfIcfVersion || DEFAULT_ICF_VERSION;
    }
    /**
     * Saves a planner configuration.
     *
     * @param configuration Configuration to save.
     * @param shoppingItems Product items.
     * @param [configurationImage] Configuration image as a base 64 string (e.g. "data:image/jpeg;base64,abc..").
     * @param insightsCombinationFormat Insights Combination Format.
     * @returns Promise object.
     */
    async saveConfiguration(configuration, shoppingItems, configurationImage, insightsCombinationFormat) {
        const response = await ServiceFetch.json(this.endpoint, {
            method: 'POST',
            body: this.getRequestBody(configuration, shoppingItems, insightsCombinationFormat),
            credentials: 'same-origin',
            headers: {
                'dexf-api-key': this.dexfApiKey,
                'content-type': 'application/json'
            }
        });
        if (response && response.configurationId) {
            const configurationResponse = {
                application: this.dexfApplicationId,
                configuration,
                configurationId: response.configurationId,
                configurationVersion: this.dexfConfigurationVersion,
                icfSaved: response.icf_saved
            };
            if (configurationImage) {
                const images = [];
                if (typeof configurationImage === 'string') {
                    images.push({
                        typeCode: 'main-design-image',
                        image: configurationImage
                    });
                }
                else if (Array.isArray(configurationImage)) {
                    images.push(...configurationImage);
                }
                const imageResponse = await Promise.all(images.map((image) => this.saveImage(response._links, image.image, image.typeCode)));
                return { ...configurationResponse, imageUrl: imageResponse };
            }
            return configurationResponse;
        }
        return null;
    }
    /**
     * Save image to vpc dexf.
     *
     * @param links Links.
     * @param configurationImage Configuration image.
     * @param typeCode Type code.
     * @returns Promise object.
     */
    async saveImage(links, configurationImage, typeCode) {
        try {
            // eslint-disable-next-line dot-notation
            const response = await ServiceFetch.json(links[typeCode]['href'], {
                method: 'POST',
                body: configurationImage,
                credentials: 'same-origin',
                headers: {
                    'dexf-api-key': this.dexfApiKey,
                    'content-type': 'text/plain;charset=UTF-8'
                }
            });
            return {
                typeCode,
                url: response.url
            };
        }
        catch (error) {
            throw new ServiceException('Failed to save image. Error: ' + error);
        }
    }
    /**
     * Fetches and returns a planner configuration.
     *
     * @param id Configuration ID (VPC Code).
     * @param options ...
     * @param options.includeIcfData Whether to include ICF data in the response or not.
     * @returns Promise object.
     */
    async getConfiguration(id, options) {
        const url = this.endpoint + '/' + id.toUpperCase();
        const response = await ServiceFetch.json(url, {
            credentials: 'same-origin',
            headers: {
                'dexf-api-key': this.dexfApiKey
            }
        });
        if (response && response.configurationId) {
            return {
                configurationId: response.configurationId,
                configurationVersion: response.configuration.version,
                application: response.application.toUpperCase(),
                configuration: response.configuration.content,
                imageUrl: response.image,
                itemList: response.itemList?.item ? this.getItemList(response.itemList.item) : null,
                ...(options?.includeIcfData && { icf: response.icf })
            };
        }
        return null;
    }
    /**
     * Return a list of shopping items.
     *
     * @param itemList ItemList.
     * @param itemList.itemType ItemType.
     * @param itemList.itemNo ItemNo.
     * @param itemList.quantity Quantity.
     */
    getItemList(itemList) {
        return itemList.map((item) => ({
            id: item.itemNo,
            type: item.itemType,
            quantity: item.quantity
        }));
    }
    /**
     * Returns payload to be used when saving a configuration.
     *
     * @param configuration Configuration to save.
     * @param shoppingItems Product items.
     * @param insightsCombinationFormat Insights Combination Format.
     * @returns Parsed data.
     */
    getRequestBody(configuration, shoppingItems, insightsCombinationFormat) {
        const body = {
            application: this.dexfApplicationId,
            itemList: {
                item: shoppingItems.map((item) => {
                    return { itemType: item.type, itemNo: item.id, quantity: item.quantity };
                })
            },
            configuration: {
                version: this.dexfConfigurationVersion,
                content: configuration
            }
        };
        if (insightsCombinationFormat) {
            Object.assign(body, {
                icf: {
                    version: this.dexfIcfVersion,
                    content: insightsCombinationFormat
                }
            });
        }
        return JSON.stringify(body);
    }
}
//# sourceMappingURL=DexfVpcService.js.map